import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  IconButton,
  Modal,
  CircularProgress,
  Chip,
  Tooltip,
  FormControl,
  FormLabel,
  Textarea,
  Button,
  Checkbox,
  Sheet,
} from '@mui/joy';
import { X, CaretLeft as ChevronLeft, CaretRight as ChevronRight, Calendar, Tag, DownloadSimple as Download, ArrowsOut as Maximize2, Trash as Trash2, PencilSimple as Edit2, WarningCircle, DotsSixVertical, PaperPlaneTilt } from '@phosphor-icons/react';
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Photo, Tag as TagType } from '../types';
import { apiClient } from '../lib/api';
import { TagPicker } from './tags/TagPicker';
import { useTags, useCreateTag } from '../hooks/useTags';
import { AnnotatedPhoto } from './photos/AnnotatedPhoto';
import { PhotoAnnotationEditor } from './photos/PhotoAnnotationEditor';
import { hasAnnotations } from '../shared/photo-annotations/annotationMigrate';
import { PhotoFolderToolbar, type FolderSelection } from './photos/PhotoFolderToolbar';
import { EmailPhotosModal } from './photos/EmailPhotosModal';

interface PhotoGalleryProps {
  photos: Photo[];
  loading?: boolean;
  onPhotoUpdate?: () => void;
  onPhotoDelete?: () => void;
  /**
   * The record these photos belong to. Passing one turns on drag-to-reorder
   * and emailing - both need to know where the order is saved and who the
   * default recipient is. Without it the gallery behaves exactly as before.
   */
  opportunityId?: number;
  jobId?: number;
  allowReorder?: boolean;
}

/**
 * A photo row is created when the upload is presigned, before the file itself
 * exists. Until the server finishes moving it into place there is nothing to
 * point an <img> at, and rendering one anyway produced a grid of broken-image
 * icons. Returns null while a photo is still on its way.
 */
const getPhotoSrc = (photo: Photo): string | null =>
  photo.thumbnail_url || photo.image_url || photo.image || null;

const isPhotoProcessing = (photo: Photo): boolean =>
  !getPhotoSrc(photo) && photo.upload_status !== 'failed';

const isPhotoFailed = (photo: Photo): boolean =>
  !getPhotoSrc(photo) && photo.upload_status === 'failed';

interface PhotoTileProps {
  photo: Photo;
  sortable: boolean;
  selectionMode: boolean;
  isSelected: boolean;
  onToggleSelect: (photoId: number) => void;
  onOpen: () => void;
  onDelete: (photoId: number, e: React.MouseEvent) => void;
  onEdit: (photo: Photo, e: React.MouseEvent) => void;
  isDeleting: boolean;
  formatDate: (value: string) => string;
}

/**
 * One thumbnail. Split out of the grid so it can own a useSortable hook -
 * hooks cannot be called inside a .map, and reordering needs one per tile.
 */
const PhotoTile: React.FC<PhotoTileProps> = ({
  photo,
  sortable,
  selectionMode,
  isSelected,
  onToggleSelect,
  onOpen,
  onDelete,
  onEdit,
  isDeleting,
  formatDate,
}) => {
  const [hovered, setHovered] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: photo.id,
    disabled: !sortable,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        p: 0,
        overflow: 'hidden',
        cursor: selectionMode || getPhotoSrc(photo) ? 'pointer' : 'default',
        transition: 'all 0.2s',
        outline: isSelected ? '2px solid' : undefined,
        outlineColor: 'primary.500',
        outlineOffset: '-2px',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 'md',
        },
      }}
      onClick={() => {
        if (selectionMode) {
          onToggleSelect(photo.id);
          return;
        }
        // Nothing to show in the lightbox until the file lands.
        if (getPhotoSrc(photo)) onOpen();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
            <Box
              sx={{
                position: 'relative',
                paddingTop: '100%', // 1:1 aspect ratio
                bgcolor: 'background.level2',
              }}
            >
              {hasAnnotations(photo.annotations) && (
                <Chip size="sm" variant="solid" color="primary"
                  sx={{ position: 'absolute', bottom: 8, left: 8, zIndex: 1 }}>
                  Annotated
                </Chip>
              )}
              {getPhotoSrc(photo) ? (
                <img
                  src={getPhotoSrc(photo) as string}
                  alt={photo.description || `Photo ${photo.id}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  loading="lazy"
                />
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    p: 1,
                    textAlign: 'center',
                  }}
                >
                  {isPhotoFailed(photo) ? (
                    <>
                      <WarningCircle size={24} weight="duotone" />
                      <Typography level="body-xs" sx={{ color: 'danger.plainColor' }}>
                        Upload failed
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CircularProgress size="sm" />
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Processing…
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              {/* Selection checkbox. Visible while selecting, and on hover so
                  the first photo can be picked without flipping a mode first. */}
              {(selectionMode || hovered) && getPhotoSrc(photo) && (
                <Checkbox
                  checked={isSelected}
                  variant="solid"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    '& .MuiCheckbox-checkbox': { bgcolor: 'background.surface' },
                  }}
                  slotProps={{ input: { 'aria-label': `Select photo ${photo.id}` } }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect(photo.id);
                  }}
                  onChange={() => undefined}
                />
              )}

              {/* Drag handle. Its own control rather than the whole tile, so a
                  tap still opens the photo and dragging stays deliberate. */}
              {sortable && !selectionMode && (
                <Tooltip title="Drag to reorder">
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="neutral"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      cursor: 'grab',
                      opacity: hovered ? 0.95 : 0.65,
                      touchAction: 'none',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    {...attributes}
                    {...listeners}
                  >
                    <DotsSixVertical size={16} />
                  </IconButton>
                </Tooltip>
              )}

              {/* HEIC conversion status indicator */}
              {photo.is_heic && photo.conversion_status === 'pending' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'warning.100',
                    color: 'warning.700',
                    px: 1,
                    py: 0.5,
                    borderRadius: 'sm',
                    fontSize: 'xs',
                    fontWeight: 'md',
                  }}
                >
                  Converting...
                </Box>
              )}
              {photo.is_heic && photo.conversion_status === 'processing' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'primary.100',
                    color: 'var(--brand-text, #7013f5)',
                    px: 1,
                    py: 0.5,
                    borderRadius: 'sm',
                    fontSize: 'xs',
                    fontWeight: 'md',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <CircularProgress size="sm" />
                  Processing...
                </Box>
              )}
              {photo.is_heic && photo.conversion_status === 'failed' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'danger.100',
                    color: 'danger.700',
                    px: 1,
                    py: 0.5,
                    borderRadius: 'sm',
                    fontSize: 'xs',
                    fontWeight: 'md',
                  }}
                >
                  Failed
                </Box>
              )}

              {/* Action buttons on hover */}
              {hovered && !selectionMode && (
                <>
                  {/* Delete button */}
                  <Tooltip title="Delete photo">
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'danger.500',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'danger.600',
                        },
                        opacity: 0.9,
                      }}
                      size="sm"
                      onClick={(e) => onDelete(photo.id, e)}
                      disabled={isDeleting}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Tooltip>

                  {/* Edit button */}
                  <Tooltip title="Edit photo">
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 48,
                        bgcolor: 'var(--brand-text, #7013f5)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'var(--brand-text, #7013f5)',
                        },
                        opacity: 0.9,
                      }}
                      size="sm"
                      onClick={(e) => onEdit(photo, e)}
                    >
                      <Edit2 size={16} />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0)',
                  transition: 'background-color 0.2s',
                  pointerEvents: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                  },
                }}
              >
                <IconButton
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    '.MuiCard-root:hover &': {
                      opacity: 1,
                    },
                  }}
                  variant="soft"
                  color="neutral"
                  size="lg"
                >
                  <Maximize2 />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ p: 1 }}>
              <Typography level="body-xs" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Calendar size={12} />
                {formatDate(photo.uploaded_at)}
              </Typography>
              {photo.tags.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  {(photo.tags || []).slice(0, 2).map((tag) => (
                    <Chip key={tag.id} size="sm" variant="soft" sx={{ fontSize: '10px' }}>
                      {tag.name}
                    </Chip>
                  ))}
                  {photo.tags.length > 2 && (
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      +{photo.tags.length - 2}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
    </Card>
  );
};

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  photos,
  loading = false,
  onPhotoUpdate,
  onPhotoDelete,
  opportunityId,
  jobId,
  allowReorder = true,
}) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [annotatingPhoto, setAnnotatingPhoto] = useState<Photo | null>(null);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState<Photo | null>(null);
  const [editFormData, setEditFormData] = useState<{ description: string; tagIds: number[] }>({
    description: '',
    tagIds: []
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { data: availableTags = [] } = useTags();
  const createTag = useCreateTag();

  // Touch handling state
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);

  // Reorder + selection. Both need to know which record we are on: the order
  // is saved against it and it supplies the default email recipient.
  const parent = opportunityId ? { opportunity: opportunityId } : jobId ? { job: jobId } : null;
  const [activeFolder, setActiveFolder] = useState<FolderSelection>('all');
  const canReorder = activeFolder === 'all' && allowReorder && parent !== null && photos.length > 1;
  const [orderedPhotos, setOrderedPhotos] = useState<Photo[]>(photos);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [emailOpen, setEmailOpen] = useState(false);
  const [reorderError, setReorderError] = useState<string | null>(null);

  // Drag state is local so the grid moves under the pointer, but the server is
  // the source of truth — adopt every refetch except the one mid-drag.
  useEffect(() => {
    const visible = photos.filter(p => activeFolder === 'all' || (activeFolder === 'unfiled' ? !p.folder : p.folder === activeFolder));
    setOrderedPhotos(visible);
    // Drop selections for photos that no longer exist (deleted, or filtered out).
    setSelectedIds((prev) => {
      const live = new Set(visible.map((p) => p.id));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [photos, activeFolder]);

  const sensors = useSensors(
    // A small distance/delay threshold keeps a tap on a photo opening the
    // lightbox instead of starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } })
  );

  // Explicit rather than derived from "something is selected": on touch there
  // is no hover to reveal the first checkbox, so there has to be a way in.
  const [selecting, setSelecting] = useState(false);
  const selectionMode = selecting || selectedIds.size > 0;

  const exitSelection = useCallback(() => {
    setSelecting(false);
    setSelectedIds(new Set());
  }, []);

  const toggleSelected = useCallback((photoId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) {
        next.delete(photoId);
      } else {
        next.add(photoId);
      }
      return next;
    });
  }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !parent || !canReorder) return;

    const oldIndex = orderedPhotos.findIndex((p) => p.id === active.id);
    const newIndex = orderedPhotos.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previous = orderedPhotos;
    const reordered = arrayMove(orderedPhotos, oldIndex, newIndex);
    setOrderedPhotos(reordered);
    setReorderError(null);

    try {
      await apiClient.reorderPhotos({ ...parent, photo_ids: reordered.map((p) => p.id) });
      onPhotoUpdate?.();
    } catch (error) {
      // Put the photo back where it was rather than leaving the screen
      // showing an order the server did not accept.
      setOrderedPhotos(previous);
      setReorderError('Could not save the new order. Please try again.');
      console.error('Failed to reorder photos:', error);
    }
  };

  // Auto-refresh for uploads still landing, HEIC conversions AND thumbnail generation
  useEffect(() => {
    // Check if there are any photos still converting or generating thumbnails
    const processingPhotos = photos.filter(p =>
      isPhotoProcessing(p) ||
      (p.is_heic && (p.conversion_status === 'pending' || p.conversion_status === 'processing')) ||
      (p.thumbnail_status === 'pending' || p.thumbnail_status === 'processing')
    );

    if (processingPhotos.length > 0 && onPhotoUpdate) {
      // Set up polling every 3 seconds (faster for thumbnail generation)
      const interval = setInterval(() => {
        onPhotoUpdate();
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [photos, onPhotoUpdate]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedPhotoIndex === null || annotatingPhoto) return;

      switch (e.key) {
        case 'Escape':
          setSelectedPhotoIndex(null);
          break;
        case 'ArrowLeft':
          navigateToPrevious();
          break;
        case 'ArrowRight':
          navigateToNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhotoIndex, orderedPhotos.length, annotatingPhoto]);

  const navigateToPrevious = useCallback(() => {
    if (selectedPhotoIndex === null || annotatingPhoto) return;
    const newIndex = selectedPhotoIndex === 0 ? orderedPhotos.length - 1 : selectedPhotoIndex - 1;
    setSelectedPhotoIndex(newIndex);
    setImageError(false);
  }, [selectedPhotoIndex, orderedPhotos.length, annotatingPhoto]);

  const navigateToNext = useCallback(() => {
    if (selectedPhotoIndex === null || annotatingPhoto) return;
    const newIndex = selectedPhotoIndex === orderedPhotos.length - 1 ? 0 : selectedPhotoIndex + 1;
    setSelectedPhotoIndex(newIndex);
    setImageError(false);
  }, [selectedPhotoIndex, orderedPhotos.length, annotatingPhoto]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDownload = (photo: Photo) => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = photo.image_url || photo.image || '';
    link.download = `photo-${photo.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeletePhoto = async (photoId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the lightbox

    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setIsDeleting(true);
    try {
      await apiClient.delete(`/api/photos/${photoId}/`);
      if (onPhotoDelete) {
        onPhotoDelete();
      }
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Failed to delete photo. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditPhoto = (photo: Photo, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the lightbox
    setEditingPhoto(photo);
    setEditFormData({
      description: photo.description || '',
      tagIds: (photo.tags || []).map(tag => tag.id)
    });
  };

  const handleCreateTag = async (name: string, color: string): Promise<TagType> => {
    return createTag.mutateAsync({ name, color });
  };

  const handleSavePhotoEdit = async () => {
    if (!editingPhoto) return;

    // Convert tag IDs back to comma-separated names for the API
    const tagNames = editFormData.tagIds
      .map(id => availableTags.find(t => t.id === id)?.name)
      .filter((n): n is string => n != null)
      .join(', ');

    setIsSaving(true);
    try {
      await apiClient.patch(`/api/photos/${editingPhoto.id}/`, {
        description: editFormData.description,
        tag_names: tagNames
      });

      setEditingPhoto(null);
      if (onPhotoUpdate) {
        onPhotoUpdate();
      }
    } catch (error) {
      console.error('Failed to update photo:', error);
      alert('Failed to update photo. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Touch handlers for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    // Calculate swipe distance
    const horizontalSwipe = touchStart.x - touchEnd.x;
    const verticalSwipe = Math.abs(touchStart.y - touchEnd.y);

    // Minimum swipe distance (50px) and ensure it's more horizontal than vertical
    const minSwipeDistance = 50;

    if (Math.abs(horizontalSwipe) > minSwipeDistance && Math.abs(horizontalSwipe) > verticalSwipe) {
      if (horizontalSwipe > 0) {
        // Swiped left - go to next
        navigateToNext();
      } else {
        // Swiped right - go to previous
        navigateToPrevious();
      }
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };

  const selectedPhoto = selectedPhotoIndex !== null ? orderedPhotos[selectedPhotoIndex] : null;
  const selectedPhotoId = selectedPhoto?.id;
  useEffect(() => {
    if (!selectedPhotoId || annotatingPhoto) return;
    let active = true;
    let request = 0;
    const refresh = async () => {
      const currentRequest = ++request;
      try {
        const { data } = await apiClient.get<Photo>(`/api/photos/${selectedPhotoId}/`);
        if (active && currentRequest === request) {
          setOrderedPhotos(current => current.map(photo => photo.id === data.id ? data : photo));
        }
      } catch {
        // Keep the gallery snapshot available if a background refresh fails.
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    // Mobile edits may have been saved since this gallery was loaded.
    void refresh();
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [selectedPhotoId, annotatingPhoto]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  const selectedPhotos = orderedPhotos.filter((p) => selectedIds.has(p.id));

  return (
    <>
      {parent && <PhotoFolderToolbar parent={parent} active={activeFolder} photos={photos} selectedIds={selectedIds}
        onSelect={folder => { setActiveFolder(folder); setSelectedPhotoIndex(null); exitSelection(); }}
        onChanged={() => { exitSelection(); onPhotoUpdate?.(); }} />}
      {orderedPhotos.length === 0 && <Typography sx={{ textAlign: 'center', py: 5 }}>
        {photos.length === 0 ? 'No photos uploaded yet. Create a folder to get organized.' : 'No photos in this folder. Select photos in All photos to move them here.'}
      </Typography>}
      {/* Selection bar. Appears once something is picked so the gallery stays
          uncluttered until there is an action to take. */}
      {parent && (
        selectionMode ? (
          <Sheet
            variant="soft"
            color="primary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              px: 2,
              py: 1,
              mb: 2,
              borderRadius: 'md',
            }}
          >
            <Typography level="title-sm" sx={{ mr: 'auto' }}>
              {selectedIds.size} photo{selectedIds.size === 1 ? '' : 's'} selected
            </Typography>
            <Button
              size="sm"
              variant="plain"
              // Skip photos whose file has not landed — there is nothing to send.
              onClick={() => setSelectedIds(new Set(orderedPhotos.filter(getPhotoSrc).map((p) => p.id)))}
            >
              Select all
            </Button>
            <Button size="sm" variant="plain" onClick={exitSelection}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="solid"
              startDecorator={<PaperPlaneTilt size={16} />}
              onClick={() => setEmailOpen(true)}
              disabled={selectedIds.size === 0}
            >
              Share / Export
            </Button>
          </Sheet>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
            {canReorder && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mr: 'auto', alignSelf: 'center' }}>
                Drag the handle on a photo to reorder.
              </Typography>
            )}
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              startDecorator={<PaperPlaneTilt size={16} />}
              onClick={() => setSelecting(true)}
            >
              Select photos
            </Button>
          </Box>
        )
      )}

      {reorderError && (
        <Typography level="body-sm" sx={{ color: 'danger.plainColor', mb: 1 }}>
          {reorderError}
        </Typography>
      )}

      {/* Thumbnail Grid */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedPhotos.map((p) => p.id)} strategy={rectSortingStrategy}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
          gap: 2,
        }}
      >
        {orderedPhotos.map((photo, index) => (
          <PhotoTile
            key={photo.id}
            photo={photo}
            sortable={canReorder}
            selectionMode={selectionMode}
            isSelected={selectedIds.has(photo.id)}
            onToggleSelect={toggleSelected}
            onOpen={() => {
              setSelectedPhotoIndex(index);
              setImageLoading(true);
              setImageError(false);
            }}
            onDelete={handleDeletePhoto}
            onEdit={handleEditPhoto}
            isDeleting={isDeleting}
            formatDate={formatDate}
          />
        ))}
      </Box>
        </SortableContext>
      </DndContext>

      {parent && (
        <EmailPhotosModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          photos={selectedPhotos}
          opportunityId={opportunityId}
          jobId={jobId}
          onSent={() => {
            setEmailOpen(false);
            exitSelection();
          }}
        />
      )}

      {/* Lightbox Modal */}
      {selectedPhoto && !annotatingPhoto && (
        <Modal
          open={selectedPhotoIndex !== null}
          onClose={() => setSelectedPhotoIndex(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
            }}
            onClick={() => setSelectedPhotoIndex(null)}
          >
            {/* Close button */}
            <IconButton
              variant="plain"
              color="neutral"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1,
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPhotoIndex(null);
              }}
            >
              <X />
            </IconButton>

            {/* Navigation buttons */}
            {photos.length > 1 && (
              <>
                <IconButton
                  variant="plain"
                  color="neutral"
                  size="lg"
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToPrevious();
                  }}
                >
                  <ChevronLeft size={32} />
                </IconButton>

                <IconButton
                  variant="plain"
                  color="neutral"
                  size="lg"
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToNext();
                  }}
                >
                  <ChevronRight size={32} />
                </IconButton>
              </>
            )}

            {/* Image container */}
            <Box
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Photo counter */}
              <Typography
                level="body-sm"
                sx={{ color: 'white', mb: 2, opacity: 0.8 }}
              >
                {(selectedPhotoIndex ?? 0) + 1} of {photos.length}
              </Typography>

              {/* Main image */}
              <Box
                sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {imageLoading && (
                  <CircularProgress
                    size="lg"
                    sx={{ position: 'absolute', color: 'white' }}
                  />
                )}
                {imageError ? (
                  <Box sx={{ textAlign: 'center', p: 4 }}>
                    <Typography level="body-lg" sx={{ color: 'white' }}>
                      Failed to load image
                    </Typography>
                  </Box>
                ) : (
                  <AnnotatedPhoto
                    key={selectedPhoto.id}
                    annotations={selectedPhoto.annotations}
                    showAnnotations={showAnnotations}
                    src={selectedPhoto.image_url || selectedPhoto.image}
                    alt={selectedPhoto.description || `Photo ${selectedPhoto.id}`}
                    style={{
                      maxWidth: '90vw',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      opacity: imageLoading ? 0 : 1,
                      transition: 'opacity 0.3s',
                      userSelect: 'none',
                      // @ts-expect-error WebkitUserDrag is valid but not in React.CSSProperties
                      WebkitUserDrag: 'none',
                    }}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {hasAnnotations(selectedPhoto.annotations) && (
                  <Button variant="soft" aria-pressed={showAnnotations}
                    onClick={() => setShowAnnotations(value => !value)}>
                    {showAnnotations ? 'Hide annotations' : 'Show annotations'}
                  </Button>
                )}
                <Button disabled={imageLoading || imageError}
                  onClick={() => setAnnotatingPhoto(selectedPhoto)}>
                  Edit Photo
                </Button>
              </Box>
              {/* Photo details */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                {selectedPhoto.description && (
                  <Typography level="body-md" sx={{ color: 'white', mb: 1 }}>
                    {selectedPhoto.description}
                  </Typography>
                )}
                <Typography level="body-sm" sx={{ color: 'white', opacity: 0.7 }}>
                  Uploaded {formatDate(selectedPhoto.uploaded_at)}
                </Typography>
                {selectedPhoto.tags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
                    {(selectedPhoto.tags || []).map((tag) => (
                      <Chip
                        key={tag.id}
                        variant="soft"
                        color="neutral"
                        startDecorator={<Tag size={12} />}
                      >
                        {tag.name}
                      </Chip>
                    ))}
                  </Box>
                )}

                {/* Download button */}
                <Tooltip title="Download photo">
                  <IconButton
                    variant="soft"
                    color="neutral"
                    sx={{ mt: 2 }}
                    onClick={() => handleDownload(selectedPhoto)}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Modal>
      )}

      {annotatingPhoto && (
        <PhotoAnnotationEditor key={annotatingPhoto.id} photo={annotatingPhoto}
          availableTags={availableTags}
          onCreateTag={handleCreateTag}
          onClose={() => setAnnotatingPhoto(null)}
          onSaved={saved => {
            setOrderedPhotos(current => current.map(photo => photo.id === saved.id ? saved : photo));
            setShowAnnotations(true);
            onPhotoUpdate?.();
          }} />
      )}

      {/* Edit Photo Modal */}
      {editingPhoto && (
        <Modal
          open={!!editingPhoto}
          onClose={() => setEditingPhoto(null)}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 500,
              maxWidth: '90vw',
              bgcolor: 'background.surface',
              borderRadius: 'lg',
              boxShadow: 'lg',
              p: 3,
            }}
          >
            <Typography level="h4" sx={{ mb: 3 }}>
              Edit Photo
            </Typography>

            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Description</FormLabel>
              <Textarea
                minRows={3}
                maxRows={5}
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                placeholder="Add a description..."
              />
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <TagPicker
                value={editFormData.tagIds}
                onChange={(tagIds) => setEditFormData({ ...editFormData, tagIds })}
                availableTags={availableTags}
                onCreateTag={handleCreateTag}
                placeholder="Search or create tags..."
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setEditingPhoto(null)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={handleSavePhotoEdit}
                loading={isSaving}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};
